/**
 * ============================================
 * PasswordChangeModal Component
 * ============================================
 *
 * Password change modal for authenticated users
 * Validates current password and new password confirmation
 */

import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { changePassword } from '../../lib/services/authApi';
import { AUTH_MESSAGES } from '../../constants/authMessages';
import { Modal } from '../admin/Modal';
import './PasswordChangeModal.css';

/**
 * Password change modal component
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Modal open state
 * @param {Function} props.onClose - Close handler
 */
export function PasswordChangeModal({ isOpen, onClose }) {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setErrors({});
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error on change
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.currentPassword) {
      newErrors.currentPassword = AUTH_MESSAGES.ERROR_PASSWORD_CURRENT_REQUIRED;
    }

    if (!formData.newPassword) {
      newErrors.newPassword = AUTH_MESSAGES.ERROR_PASSWORD_NEW_REQUIRED;
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = AUTH_MESSAGES.ERROR_PASSWORD_TOO_SHORT;
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = AUTH_MESSAGES.ERROR_PASSWORD_CONFIRM_REQUIRED;
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = AUTH_MESSAGES.ERROR_PASSWORD_MISMATCH;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      setLoading(true);
      await changePassword(
        formData.currentPassword,
        formData.newPassword,
        formData.confirmPassword
      );
      toast.success(AUTH_MESSAGES.SUCCESS_PASSWORD_CHANGED);
      onClose();
    } catch (err) {
      if (err.code === 'USER_PASSWORD_MISMATCH') {
        setErrors({ currentPassword: AUTH_MESSAGES.ERROR_PASSWORD_CURRENT_WRONG });
      } else if (err.code === 'USER_PASSWORD_CONFIRM_MISMATCH') {
        setErrors({ confirmPassword: AUTH_MESSAGES.ERROR_PASSWORD_MISMATCH });
      } else {
        toast.error(AUTH_MESSAGES.ERROR_PASSWORD_CHANGE_FAILED);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={AUTH_MESSAGES.PASSWORD_CHANGE_TITLE}
      size="small"
    >
      <form className="password-change-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="currentPassword">{AUTH_MESSAGES.PASSWORD_CURRENT_LABEL} *</label>
          <input
            type="password"
            id="currentPassword"
            name="currentPassword"
            className={`ds-input ${errors.currentPassword ? 'error' : ''}`}
            placeholder={AUTH_MESSAGES.PASSWORD_CURRENT_PLACEHOLDER}
            value={formData.currentPassword}
            onChange={handleChange}
            disabled={loading}
            autoComplete="current-password"
          />
          {errors.currentPassword && <span className="error-text">{errors.currentPassword}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="newPassword">{AUTH_MESSAGES.PASSWORD_NEW_LABEL} *</label>
          <input
            type="password"
            id="newPassword"
            name="newPassword"
            className={`ds-input ${errors.newPassword ? 'error' : ''}`}
            placeholder={AUTH_MESSAGES.PASSWORD_NEW_PLACEHOLDER}
            value={formData.newPassword}
            onChange={handleChange}
            disabled={loading}
            autoComplete="new-password"
          />
          {errors.newPassword && <span className="error-text">{errors.newPassword}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="confirmPassword">{AUTH_MESSAGES.PASSWORD_CONFIRM_LABEL} *</label>
          <input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            className={`ds-input ${errors.confirmPassword ? 'error' : ''}`}
            placeholder={AUTH_MESSAGES.PASSWORD_CONFIRM_PLACEHOLDER}
            value={formData.confirmPassword}
            onChange={handleChange}
            disabled={loading}
            autoComplete="new-password"
          />
          {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
        </div>

        <div className="form-actions">
          <button type="button" className="ds-button" onClick={onClose} disabled={loading}>
            취소
          </button>
          <button type="submit" className="ds-button ds-button-primary" disabled={loading}>
            {loading ? '변경 중...' : AUTH_MESSAGES.PASSWORD_CHANGE_BUTTON}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default PasswordChangeModal;
